import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiText, BiVideo } from 'react-icons/bi';

import { useAppSelector } from '@/app/hook';
import { PSInput } from '@/components/ui/input';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { selectCurrentDataState } from '../../create.slice';

const Step1 = () => {
  const { t } = useTranslation();
  const { control, reset, formState, register, setValue, watch } = useFormContext();
  const currentData = useAppSelector(selectCurrentDataState);

  const types = [
    {
      value: 'text',
      label: t('marketplace/step1/types/text/label'),
      description: t('marketplace/step1/types/text/description'),
      features: [
        t('marketplace/step1/types/text/features/0'),
        t('marketplace/step1/types/text/features/1'),
        t('marketplace/step1/types/text/features/2'),
        t('marketplace/step1/types/text/features/3'),
      ],
      icon: <BiText className="tw-w-6 tw-h-6" />,
      bgColor: 'tw-bg-blue-50',
      iconColor: 'tw-text-blue-600',
      borderColor: 'tw-border-blue-500',
      recommended: true,
    },
    {
      value: 'video',
      label: t('marketplace/step1/types/video/label'),
      description: t('marketplace/step1/types/video/description'),
      features: [
        t('marketplace/step1/types/video/features/0'),
        t('marketplace/step1/types/video/features/1'),
        t('marketplace/step1/types/video/features/2'),
        t('marketplace/step1/types/video/features/3'),
      ],
      icon: <BiVideo className="tw-w-6 tw-h-6" />,
      bgColor: 'tw-bg-purple-50',
      iconColor: 'tw-text-purple-600',
      borderColor: 'tw-border-purple-500',
      recommended: false,
    },
  ];

  useEffect(() => {
    if (currentData) {
      reset({
        name: currentData.name || '',
        version: currentData.version || '',
        type: currentData.type,
      });
    }
  }, [currentData, setValue]);

  return (
    <div className="tw-max-w-2xl tw-mx-auto tw-p-6 tw-space-y-8">
      {/* Header Section */}
      <div className="tw-text-center tw-space-y-3">
        <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-font-['Lexend']">{t('marketplace/step1/header/title')}</h1>
        <p className="tw-text-lg tw-text-gray-600 tw-font-['Lexend']">{t('marketplace/step1/header/description')}</p>
      </div>

      {/* Package Type Selection */}
      <div className="tw-space-y-4">
        <div className="tw-text-center tw-space-y-2">
          <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-font-['Lexend']">{t('marketplace/step1/typeSelection/title')}</h2>
          <p className="tw-text-gray-600 tw-font-['Lexend']">{t('marketplace/step1/typeSelection/description')}</p>
        </div>

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
          {types.map(type => {
            const isSelected = watch('type') === type.value;
            return (
              <label
                key={type.value}
                className={`tw-group tw-relative tw-flex tw-flex-col tw-p-6 tw-border-2 tw-rounded-2xl tw-cursor-pointer tw-transition-all tw-duration-300 tw-transform hover:tw-scale-[1.02] tw-shadow-sm hover:tw-shadow-lg ${
                  isSelected
                    ? `${type.borderColor} ${type.bgColor} tw-shadow-lg tw-ring-4 tw-ring-opacity-30 ${type.borderColor.replace('border', 'ring')}`
                    : 'tw-border-gray-200 tw-bg-white hover:tw-border-gray-300'
                }`}
              >
                <input {...register('type')} type="radio" value={type.value} className="tw-sr-only" />

                {/* Recommended badge */}
                {type.recommended && (
                  <div className="tw-absolute tw--top-2 tw-left-1/2 tw-transform tw--translate-x-1/2">
                    <span className="tw-bg-green-500 tw-text-white tw-text-xs tw-font-bold tw-px-3 tw-py-1 tw-rounded-full tw-font-['Lexend'] tw-shadow-md">
                      ✨ {t('marketplace/step1/badge/popular')}
                    </span>
                  </div>
                )}

                {/* Header with icon and title */}
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4 tw-mt-2">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div
                      className={`tw-p-3 tw-rounded-xl tw-transition-all tw-duration-200 tw-shadow-md ${
                        isSelected ? `${type.bgColor} ${type.iconColor}` : 'tw-bg-gray-100 tw-text-gray-600 group-hover:tw-bg-gray-200'
                      }`}
                    >
                      {type.icon}
                    </div>
                    <div>
                      <h3 className="tw-font-bold tw-text-lg tw-text-gray-900 tw-font-['Lexend']">{type.label}</h3>
                    </div>
                  </div>

                  {/* Selection indicator */}
                  <div
                    className={`tw-w-6 tw-h-6 tw-rounded-full tw-border-2 tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center tw-shadow-sm ${
                      isSelected ? `${type.borderColor} ${type.bgColor}` : 'tw-border-gray-300 tw-bg-white group-hover:tw-border-gray-400'
                    }`}
                  >
                    {isSelected && (
                      <div className={`tw-w-3 tw-h-3 tw-rounded-full tw-transition-all tw-duration-200 ${type.iconColor.replace('text', 'bg')}`}></div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <p className="tw-text-sm tw-text-gray-600 tw-font-['Lexend'] tw-leading-relaxed tw-mb-4">{type.description}</p>

                {/* Features list */}
                <div className="tw-space-y-2.5">
                  {type.features.map((feature, index) => (
                    <div key={index} className="tw-flex tw-items-center tw-gap-3">
                      <div
                        className={`tw-w-2 tw-h-2 tw-rounded-full tw-transition-colors tw-duration-200 ${
                          isSelected ? type.iconColor.replace('text', 'bg') : 'tw-bg-gray-400'
                        }`}
                      ></div>
                      <span className="tw-text-sm tw-text-gray-700 tw-font-['Lexend'] tw-font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Subtle animation overlay */}
                <div
                  className={`tw-absolute tw-inset-0 tw-rounded-2xl tw-transition-all tw-duration-300 tw-pointer-events-none ${
                    isSelected ? 'tw-opacity-0' : 'tw-opacity-0 group-hover:tw-opacity-5 tw-bg-gradient-to-br tw-from-transparent tw-to-gray-900'
                  }`}
                ></div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Package Details */}
      <div className="tw-space-y-6">
        <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-font-['Lexend']">{t('marketplace/step1/packageDetails/title')}</h2>

        <div className="tw-space-y-4">
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-font-['Lexend'] tw-mb-2">
              {t('marketplace/step1/packageDetails/name/label')}
              <span className="tw-text-red-500 tw-ml-1">*</span>
            </label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <PSInput
                  {...field}
                  value={field.value ?? ''}
                  placeholder={t('marketplace/step1/packageDetails/name/placeholder')}
                  onChange={e => {
                    // Only allow alphanumeric characters (remove all non-alphanumeric)
                    const value = e.target.value.replace(/[^a-zA-Z0-9-]/g, '');
                    setValue('name', value, { shouldValidate: true });
                  }}
                  className={`tw-border tw-border-solid tw-border-gray-300 tw-px-4 tw-py-3 tw-text-base ${
                    formState.errors?.name
                      ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                      : 'focus:tw-border-blue-500 focus:tw-ring-blue-500'
                  }`}
                />
              )}
            />
            <p className="tw-text-xs tw-text-gray-500 tw-font-['Lexend'] tw-mt-1">{t('marketplace/step1/packageDetails/name/hint')}</p>
            {formState.errors?.name && <p className="tw-text-sm tw-text-red-600 tw-font-['Lexend'] tw-mt-1">{formState.errors?.name?.message as string}</p>}
          </div>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-font-['Lexend'] tw-mb-2">
              {t('marketplace/step1/packageDetails/version/label')}
              <span className="tw-text-red-500 tw-ml-1">*</span>
            </label>
            <Controller
              name="version"
              control={control}
              render={({ field }) => (
                <PSInput
                  {...field}
                  value={field.value ?? ''}
                  type="number"
                  step="1"
                  min="1"
                  max="500"
                  placeholder={t('marketplace/step1/packageDetails/version/placeholder')}
                  inputMode="numeric"
                  pattern="^\d+$"
                  className={`tw-border tw-border-solid tw-border-gray-300 tw-px-4 tw-py-3 tw-text-base tw-w-32 ${
                    formState.errors?.version
                      ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                      : 'focus:tw-border-blue-500 focus:tw-ring-blue-500'
                  }`}
                />
              )}
            />
            <p className="tw-text-xs tw-text-gray-500 tw-font-['Lexend'] tw-mt-1">{t('marketplace/step1/packageDetails/version/hint')}</p>
            {formState.errors?.version && (
              <p className="tw-text-sm tw-text-red-600 tw-font-['Lexend'] tw-mt-1">{formState.errors?.version?.message as string}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(Step1);
